import { Alert, Box, Link as MuiLink, Typography } from "@mui/material"
import { Link as RouterLink } from "react-router-dom"

import { AccessEditor } from "../../../../../pages/documentation/access/AccessEditor.js"

/**
 * Reseller profile ▸ **Documentation** — what THIS company may read
 * (2026-09-14).
 *
 * The same editor as Documentation ▸ Access, with the account already
 * picked: the operator is on the reseller, so the question "which of our
 * articles does this partner get?" is answered where the partner is, not
 * two sections away in a rail search. The subject is addressed as
 * `crm-<id>` because this page holds the crm-api id and portal-api
 * resolves that form itself (`ResellerAccount.crm_reseller_id`).
 *
 * BLOCKED BY DEFAULT (2026-09-15): a reseller reads nothing until staff
 * allow it — library folders and release guides alike — so the editor offers
 * only Allow / Block here, and the intro says so.
 *
 * Shown only to a holder of `documentation:manage_access` — the tab set is
 * filtered on it the way the money tabs are filtered on `crm:financials`.
 */
export function ResellerKnowledgeBaseTab({
  resellerId,
  resellerName,
}: {
  resellerId: string
  resellerName: string
}) {
  return (
    <Box sx={{ display: "grid", gap: 2 }} data-cy="reseller-knowledge-base-tab">
      <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
        The documentation as {resellerName} sees it on its portal. Everything is blocked until you
        allow it — a folder, or a single document, including release guides. Block keeps one document
        hidden inside an allowed folder. A folder must also be published to resellers in the library.{" "}
        <MuiLink component={RouterLink} to="/documentation/access" underline="hover">
          Open Documentation ▸ Access
        </MuiLink>{" "}
        to curate every account side by side.
      </Typography>

      <AccessEditor
        key={resellerId}
        subjectType="reseller"
        subjectId={`crm-${resellerId}`}
        notFound={
          <Alert severity="info" data-cy="reseller-knowledge-base-unsynced">
            {resellerName} has not reached the support portal yet, so there is nothing to curate.
            The reseller catalog syncs on the next snapshot; until then this company cannot sign in
            to the documentation either.
          </Alert>
        }
      />
    </Box>
  )
}
